import { useContext, useState, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { DataContext } from '@aytm/app/stats/page/sidebar/coding/CodingPanel'

import { decodeText, pluralize } from '@aytm/app/stats/lib/util/helpers'

import { getCodebooksItemOptionsList } from '@aytm/app/stats/page/comments/lib/utils'

import SettingsButton from '@aytm/app/stats/page/sidebar/coding/components/SettingsButton'
import { countReportsUsingCodebook } from '@aytm/app/stats/page/sidebar/coding/lib/used-reports'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

let statusTimer = null

const CodebookSettings = ({
  codebookRef,
  codebook,
  opened = false,
  isEditing = false,
  isProcessing = false,
  isCurrentProcessing = false,
  onConfirm
}) => {
  const {
    features,
    reports,
    reportId,
    selectedQuestion,
    isSkipperDisabled,
    onAction,
    onCloseDropdown,
    onCancelEditing,
    onToggleSettings
  } = useContext(DataContext)

  const [selectedAction, setSelectedAction] = useState(null)
  const [selectedActionStatus, setSelectedActionStatus] = useState(null)
  const [usedReportsCount, setUsedReportsCount] = useState(0)

  const onActionCancel = useCallback(() => {
    setSelectedAction(null)
    if (isEditing) onCancelEditing()
  }, [isEditing, onCancelEditing])

  const onActionConfirm = useCallback(() => {
    onConfirm(selectedAction)
  }, [selectedAction, onConfirm])

  const onActionClick = useCallback(
    action => {
      switch (action) {
        case 'copy': {
          statusTimer && clearTimeout(statusTimer)

          const text = [decodeText(codebook.codebook)].concat(codebook.tags.map(tag => decodeText(tag))).join('\n')

          if (window.navigator?.clipboard) {
            const type = 'text/plain'
            const blob = new Blob([text], { type })
            const data = [new ClipboardItem({ [type]: blob })]
            window.navigator.clipboard.write(data)
          } else {
            const textarea = document.createElement('textarea')
            textarea.value = text
            textarea.style.position = 'fixed'
            textarea.style.left = '-999999px'
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
          }

          setSelectedActionStatus('copied')

          break
        }
        case 'skipper-apply':
        case 'skipper-guided': {
          dispatchEvent(
            new CustomEvent('assignCodingSkipper', { detail: { question: selectedQuestion, action, codebook } })
          )

          onCloseDropdown()
          onCancelEditing()

          break
        }
        default: {
          if ('assign' != action) setSelectedAction(action)
          if (onAction) onAction(action, codebook)
        }
      }
    },
    [codebook, selectedQuestion, onAction, onCloseDropdown, onCancelEditing]
  )
  const options = useMemo(
    () =>
      getCodebooksItemOptionsList({
        codebook,
        selectedQuestion,
        selectedAction,
        selectedActionStatus,
        isSkipperDisabled,
        isAiAssistantsOECodingDisabled: features.ai_assistants_oe_coding.disabled,
        onActionCancel,
        onActionConfirm,
        onActionClick
      }),
    [
      codebook,
      selectedQuestion,
      selectedAction,
      selectedActionStatus,
      isSkipperDisabled,
      features.ai_assistants_oe_coding.disabled,
      onActionCancel,
      onActionConfirm,
      onActionClick
    ]
  )

  const onToggle = useCallback(() => onToggleSettings(codebook.codebook, codebook.type), [codebook, onToggleSettings])

  useEffect(() => {
    statusTimer && clearTimeout(statusTimer)
    statusTimer = setTimeout(() => setSelectedActionStatus(null), 1500)

    return () => statusTimer && clearTimeout(statusTimer)
  }, [selectedActionStatus])

  useEffect(() => {
    if (['update', 'delete'].includes(selectedAction)) {
      setUsedReportsCount(countReportsUsingCodebook(reports, codebook))
    } else setUsedReportsCount(0)
  }, [codebook.codebook, codebook.tags, reportId, reports, selectedAction])

  return 0 < options.length ? (
    <SettingsButton
      itemRef={codebookRef}
      options={options}
      warning={
        selectedAction && ['update', 'delete'].includes(selectedAction) && 0 < usedReportsCount
          ? `This codebook or its codes is used in ${usedReportsCount} ${pluralize(usedReportsCount, 'report', 'reports')}. They will also be affected.`
          : null
      }
      opened={opened}
      isEditing={isEditing}
      isProcessing={isProcessing}
      isCurrentProcessing={isCurrentProcessing}
      onActionCancel={onActionCancel}
      onToggle={onToggle}
    />
  ) : (
    <div className={style['component-settings']}></div>
  )
}

CodebookSettings.propTypes = {
  codebookRef: PropTypes.any,
  codebook: PropTypes.object,
  opened: PropTypes.bool,
  isEditing: PropTypes.bool,
  isProcessing: PropTypes.bool,
  isCurrentProcessing: PropTypes.bool,
  onConfirm: PropTypes.func
}

CodebookSettings.displayName = 'CodebookSettings'

export default CodebookSettings
